import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { logger } from '../../datadog/logger';
import { CallbackError, CallbackSuccess } from '../../types';
import { useStoredAudioVideoSettings } from '../../video-sdk';
import { baseVideoSDKAdapterAtom } from '../../video-sdk/core/atoms';
import { ZoomVideoSDKAdapter } from '../../video-sdk/zoom/adapter';
import { NoiseCancellationCallbackResponseCode } from './useNoiseCancellationCallbacks';

type NoiseCancellationCallbackResponse =
  | CallbackSuccess<null, NoiseCancellationCallbackResponseCode>
  | CallbackError<NoiseCancellationCallbackResponseCode>;

export function useZoomNoiseCancellationCallbacks() {
  const adapter = useAtomValue(baseVideoSDKAdapterAtom) as ZoomVideoSDKAdapter;
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  const enableNoiseCancellation = useCallback(async (): Promise<NoiseCancellationCallbackResponse> => {
    try {
      await adapter?.setNoiseSuppression(true);
    } catch (e) {
      const error = e as Error;
      logger.error('Failed to enable Zoom noise cancellation', {}, error);
      return {
        status: 'error',
        code: NoiseCancellationCallbackResponseCode.ERROR,
        value: error,
      };
    }
    setStoredAudioVideoSettings((prev) => ({
      ...prev,
      noiseCancellationIsEnabled: true,
    }));
    return { status: 'success', value: null };
  }, [adapter, setStoredAudioVideoSettings]);

  const disableNoiseCancellation = useCallback(async (): Promise<NoiseCancellationCallbackResponse> => {
    try {
      await adapter?.setNoiseSuppression(false);
    } catch (e) {
      const error = e as Error;
      logger.error('Failed to disable Zoom noise cancellation', {}, error);
      return {
        status: 'error',
        code: NoiseCancellationCallbackResponseCode.ERROR,
        value: error,
      };
    }
    setStoredAudioVideoSettings((prev) => ({
      ...prev,
      noiseCancellationIsEnabled: false,
    }));
    return { status: 'success', value: null };
  }, [adapter, setStoredAudioVideoSettings]);

  return { enableNoiseCancellation, disableNoiseCancellation };
}
